// src/components/FormComponents/PlanCardsSelect.tsx
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export type PlanItem = {
  value: string;
  title: string;
  discountText: string;
  descriptionLines: string[];
  ctaText: string;
  ctaDocPdf: string;
};

export function PlanCardsSelect({
  items,
  value,
  onChange,
  error,
  onCtaClick,
}: {
  items: PlanItem[];
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  onCtaClick?: (args: { planValue: string; ctaDocPdf: string }) => void;
}) {
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isDesktopUp = useMediaQuery(theme.breakpoints.up("md"));
  const cardsPerView = isDesktopUp ? 3 : isTabletUp ? 2 : 1;
  const maxSlide = Math.max(0, items.length - cardsPerView);
  const [activeSlide, setActiveSlide] = useState(0);
  const showControls = items.length > cardsPerView;
  const cardWidth = `${100 / cardsPerView}%`;
  const pageIndexes = useMemo(
    () => Array.from({ length: maxSlide + 1 }, (_, index) => index),
    [maxSlide],
  );

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (nextValue !== value) onChange(nextValue);
    },
    [onChange, value],
  );

  const moveSlide = useCallback(
    (offset: number) => {
      setActiveSlide((current) =>
        Math.min(maxSlide, Math.max(0, current + offset)),
      );
    },
    [maxSlide],
  );

  return (
    <Box
      sx={{
        direction: "rtl",
        width: "100%",
        position: "relative",
      }}
      role="radiogroup"
      aria-invalid={!!error}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 1.5 },
        }}
      >
        {showControls && (
          <IconButton
            type="button"
            aria-label="למסלול הקודם"
            onClick={() => moveSlide(-1)}
            disabled={activeSlide === 0}
            sx={{
              display: "inline-flex",
              width: { xs: 34, sm: 40 },
              height: { xs: 34, sm: 40 },
              border: "1px solid",
              borderColor: "primary.main",
              color: "primary.main",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "rgba(1, 241, 86, 0.08)" },
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        )}

        <Box
          sx={{
            overflow: "hidden",
            flex: 1,
            px: { xs: 0, sm: 0.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              direction: "ltr",
              transform: `translateX(-${activeSlide * (100 / cardsPerView)}%)`,
              transition: "transform 260ms ease",
            }}
          >
        {items.map((item) => {
          const selected = item.value === value;

          return (
            <Box
              key={item.value}
              sx={{
                flex: `0 0 ${cardWidth}`,
                maxWidth: cardWidth,
                px: { xs: 0.5, sm: 0.75 },
                direction: "rtl",
              }}
            >
              <Card
                variant="outlined"
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => handleSelect(item.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(item.value);
                  }
                }}
                sx={{
                  height: "100%",
                  minHeight: { xs: 410, sm: 430 },
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: "common.white",
                  color: "text.primary",
                  borderRadius: 4,
                  overflow: "hidden",
                  borderColor: error
                    ? "error.main"
                    : selected
                      ? "success.main"
                      : "rgba(0,0,0,0.12)",
                  boxShadow: selected
                    ? "0 0 0 4px rgba(1, 241, 86, 0.28), 0 18px 36px rgba(0,0,0,0.16)"
                    : "0 12px 28px rgba(0,0,0,0.12)",
                  transition:
                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                  "&:hover": { transform: "translateY(-2px)" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: selected ? "success.main" : "common.white",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    height: "100%",
                    direction: "rtl",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: { xs: 190, sm: 205 },
                      p: { xs: 2.5, sm: 3 },
                      color: "common.black",
                      bgcolor: "#01F156",
                      borderBottomLeftRadius: 34,
                      borderBottomRightRadius: 34,
                      gap: 0.25,
                      direction: "rtl",
                    }}
                  >
                    <Typography
                      dir="rtl"
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        direction: "rtl",
                        fontSize: { xs: 23, sm: 26 },
                        lineHeight: 1.1,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Box
                      dir="rtl"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        direction: "rtl",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: 78, sm: 88 },
                          fontWeight: 900,
                          lineHeight: 0.95,
                          letterSpacing: 0,
                        }}
                      >
                        {item.discountText}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: 42, sm: 48 },
                          fontWeight: 900,
                          lineHeight: 1,
                          letterSpacing: 0,
                        }}
                      >
                        הנחה
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    dir="rtl"
                    sx={{
                      flex: 1,
                      width: "100%",
                      px: { xs: 2.25, sm: 2.75 },
                      py: { xs: 2.25, sm: 2.5 },
                      direction: "rtl",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {item.descriptionLines.map((line, idx) => (
                      <Typography
                        dir="rtl"
                        key={`${item.value}-${idx}`}
                        variant="body2"
                        sx={{
                          lineHeight: 1.28,
                          fontSize: { xs: 19, sm: 21 },
                          fontWeight: idx === 0 ? 800 : 600,
                          color: idx === 0 ? "text.primary" : "text.secondary",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>

                  <Button
                    component="a"
                    href={item.ctaDocPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCtaClick?.({
                        planValue: item.value,
                        ctaDocPdf: item.ctaDocPdf,
                      });
                    }}
                    sx={{
                      mx: { xs: 2.25, sm: 2.75 },
                      mb: { xs: 2.25, sm: 2.75 },
                      bgcolor: "#01F156",
                      color: "common.black",
                      fontWeight: 900,
                      borderRadius: 999,
                      fontSize: { xs: 17, sm: 19 },
                      py: 1.05,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#00d94d", boxShadow: "none" },
                    }}
                  >
                    {item.ctaText}
                  </Button>
                </Box>
              </Card>
            </Box>
          );
        })}
          </Box>
        </Box>

        {showControls && (
          <IconButton
            type="button"
            aria-label="למסלול הבא"
            onClick={() => moveSlide(1)}
            disabled={activeSlide === maxSlide}
            sx={{
              display: "inline-flex",
              width: { xs: 34, sm: 40 },
              height: { xs: 34, sm: 40 },
              border: "1px solid",
              borderColor: "primary.main",
              color: "primary.main",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "rgba(1, 241, 86, 0.08)" },
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        )}
      </Box>

      {showControls && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mt: 2,
          }}
        >
          {pageIndexes.map((pageIndex) => (
            <Box
              component="button"
              key={pageIndex}
              type="button"
              aria-label={`עמוד ${pageIndex + 1}`}
              aria-current={activeSlide === pageIndex ? "true" : undefined}
              onClick={() => setActiveSlide(pageIndex)}
              sx={{
                width: activeSlide === pageIndex ? 28 : 9,
                height: 9,
                border: 0,
                borderRadius: 999,
                p: 0,
                cursor: "pointer",
                bgcolor:
                  activeSlide === pageIndex ? "primary.main" : "grey.300",
                transition: "all 160ms ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
